import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdminPaymentVerifications } from '@/hooks/usePaymentProofs'
import { PageHeader } from '@/components/layout'
import type { PaymentProofWithRelations } from '@/types/paymentProof'

const statusOptions = [
  { value: '', label: 'Semua' },
  { value: 'pending', label: 'Menunggu' },
  { value: 'approved', label: 'Disetujui' },
  { value: 'rejected', label: 'Ditolak' },
  { value: 'cancelled', label: 'Dibatalkan' },
  { value: 'expired', label: 'Kedaluwarsa' },
]

const statusBadge: Record<string, { label: string; className: string }> = {
  pending: { label: 'Menunggu', className: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400' },
  approved: { label: 'Disetujui', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400' },
  rejected: { label: 'Ditolak', className: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400' },
  cancelled: { label: 'Dibatalkan', className: 'bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-text-muted' },
  expired: { label: 'Kedaluwarsa', className: 'bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-text-muted' },
}

export const AdminPaymentVerificationsPage: React.FC = () => {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useAdminPaymentVerifications(statusFilter, page)
  const pendingCount = data?.pendingCount

  const proofs: PaymentProofWithRelations[] = data?.items || []
  const total = data?.total || 0
  const totalPages = Math.ceil(total / 20)

  return (
    <div>
      <PageHeader
        title="Verifikasi Pembayaran"
        subtitle={pendingCount && pendingCount > 0 ? `${pendingCount} bukti pembayaran menunggu verifikasi` : 'Kelola verifikasi bukti pembayaran manual'}
      />

      {/* Filter */}
      <div className="flex gap-2 mb-6">
        {statusOptions.map((opt) => (
          <button
            key={opt.value}
            onClick={() => { setStatusFilter(opt.value); setPage(1) }}
            className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
              statusFilter === opt.value
                ? 'bg-primary text-white shadow-lg shadow-primary/20'
                : 'bg-white dark:bg-surface-card text-gray-700 dark:text-white border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="bg-white dark:bg-surface-card rounded-xl border border-gray-200 dark:border-white/5 p-8">
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-100 dark:bg-white/5 rounded-lg" />
            ))}
          </div>
        </div>
      ) : proofs.length === 0 ? (
        <div className="bg-white dark:bg-surface-card rounded-xl border border-gray-200 dark:border-white/5 p-8 text-center">
          <span className="material-symbols-outlined text-4xl text-gray-300 dark:text-white/20 mb-3">verified</span>
          <p className="text-gray-500 dark:text-text-muted text-sm">Tidak ada data verifikasi</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-surface-card rounded-xl border border-gray-200 dark:border-white/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
            <thead className="bg-gray-50 dark:bg-white/5 border-b border-gray-200 dark:border-white/5">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider">Peserta</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider">Course</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider">Jumlah</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider">Tanggal</th>
                <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-white/5">
              {proofs.map((proof) => {
                const badge = statusBadge[proof.status] || statusBadge.pending
                return (
                  <tr
                    key={proof.id}
                    role="button"
                    tabIndex={0}
                    className="hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/payment-verifications/${proof.id}`)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            navigate(`/admin/payment-verifications/${proof.id}`)
                        }
                    }}
                  >
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{proof.userName}</p>
                      <p className="text-xs text-gray-500 dark:text-text-muted">{proof.userEmail}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{proof.courseName}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      Rp {proof.amountPaid.toLocaleString('id-ID')}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${badge.className}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-text-muted">
                      {new Date(proof.createdAt).toLocaleDateString('id-ID')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button className="text-xs text-primary hover:text-red-800 dark:hover:text-red-400 font-medium">
                        Review
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-white/5">
              <p className="text-xs text-gray-500 dark:text-text-muted">
                Halaman {page} dari {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-xs border border-gray-200 dark:border-white/10 rounded-lg disabled:opacity-50 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-white/5"
                >
                  Sebelumnya
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 text-xs border border-gray-200 dark:border-white/10 rounded-lg disabled:opacity-50 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-white/5"
                >
                  Selanjutnya
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
